import { baseDateValues } from './settings';
import CustomDate from './CustomDate';
/**
 * Converts scroll position to date based on interval ticks
 * @param {number} scrollPosition - Current scroll position in pixels
 * @param {object} interval - Current interval object with value and pixelWidth properties
 * @param {Date|CustomDate|number} baseDate - Reference date
 * @returns {CustomDate} Calculated date
 */
export function scrollPositionToDate(scrollPosition, interval) {
  // Ensure baseDate is properly handled
  let baseDateObj = new CustomDate(baseDateValues.year, baseDateValues.month, baseDateValues.day);

  
  // Calculate how many ticks were scrolled
  const ticksScrolled = scrollPosition / interval.pixelWidth;
  
  // Round to avoid floating-point precision issues
  const roundedTicks = Math.round(ticksScrolled * 10000) / 10000;
  
  // Add that many intervals to the base date
  return baseDateObj.addIntervals(roundedTicks, interval.value);
}

/**
 * Converts a date to scroll position
 * @param {Date|CustomDate} targetDate - The date to convert to scroll position
 * @param {object} interval - Current interval object with value and pixelWidth properties
 * @param {Date|CustomDate|number} baseDate - Reference date
 * @returns {number} Calculated scroll position in pixels
 */
export function dateToScrollPosition(targetDate, interval, baseDate) {
  // Handle null or undefined dates
  if (targetDate === null || targetDate === undefined) {
    console.warn('dateToScrollPosition called with null/undefined targetDate, returning 0');
    return 0;
  }

  // Convert dates to CustomDate if needed
  let targetCustomDate;
  if (targetDate instanceof CustomDate) {
    targetCustomDate = targetDate;
  } else if (targetDate instanceof Date) {
    targetCustomDate = new CustomDate(
      targetDate.getFullYear(), 
      targetDate.getMonth(), 
      targetDate.getDate(),
      targetDate.getHours(),
      targetDate.getMinutes()
    );
  } else if (typeof targetDate === 'object' && targetDate !== null && targetDate.year !== undefined) {
    // Handle {year, month, day} format - explicitly check targetDate is not null
    targetCustomDate = new CustomDate(
      targetDate.year,
      targetDate.month || 0,
      targetDate.day || 1
    );
  } else {
    console.error('Invalid targetDate format:', targetDate);
    return 0;
  }
  
  // Ensure baseDate is properly handled
  let baseDateObj = new CustomDate(baseDateValues.year, baseDateValues.month, baseDateValues.day);
  
  // Calculate interval offset based on the interval type
  let intervalOffset = 0;
  
  switch (interval.value) {
    case '6hours':
      // Calculate 6-hour intervals between dates
      intervalOffset = (targetCustomDate.getTime() - baseDateObj.getTime()) / (6 * 60 * 60 * 1000);
      break;
      
    case 'day':
      // Calculate days between dates
      intervalOffset = (targetCustomDate.getTime() - baseDateObj.getTime()) / (24 * 60 * 60 * 1000);
      break;
      
    case 'week':
      // Calculate weeks between dates
      intervalOffset = (targetCustomDate.getTime() - baseDateObj.getTime()) / (7 * 24 * 60 * 60 * 1000);
      break;
      
    case '2weeks':
      // Calculate 2-week intervals between dates
      intervalOffset = (targetCustomDate.getTime() - baseDateObj.getTime()) / (14 * 24 * 60 * 60 * 1000);
      break;
      
    case 'month':
      // Calculate months between dates mathematically
      const yearDiff = targetCustomDate.getFullYear() - baseDateObj.getFullYear();
      const monthDiff = targetCustomDate.getMonth() - baseDateObj.getMonth();
      intervalOffset = (yearDiff * 12) + monthDiff;
      
      // Add fraction for partial months
      const dayOfMonth = targetCustomDate.getDate();
      const daysInMonth = targetCustomDate.getDaysInMonth();
      const dayFraction = (dayOfMonth - 1) / daysInMonth;
      intervalOffset += dayFraction;
      break;

    case '3months':
      // Calculate quarters between dates
      const yearDiffQuarters = targetCustomDate.getFullYear() - baseDateObj.getFullYear();
      const monthDiffQuarters = targetCustomDate.getMonth() - baseDateObj.getMonth();
      intervalOffset = (yearDiffQuarters * 4) + (monthDiffQuarters / 3);
      break;
      
    case 'year':
      // Calculate years between dates
      intervalOffset = targetCustomDate.getFullYear() - baseDateObj.getFullYear();
      
      // Add fraction for partial years
      const monthOfYear = targetCustomDate.getMonth();
      intervalOffset += monthOfYear / 12;
      break;

    case '2years':
      // Calculate 2-year intervals between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 2;
      break;

    case '5years':
      // Calculate 5-year intervals between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 5;
      break;
      
    case '10years':
      // Calculate decades between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 10;
      break;

    case '20years':
      // Calculate 20-year intervals between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 20;
      break;

    case '50years':
      // Calculate 50-year intervals between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 50;
      break;
      
    case '100years':
      // Calculate centuries between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 100;
      break;
      
    case '1000years':
      // Calculate millennia between dates
      intervalOffset = (targetCustomDate.getFullYear() - baseDateObj.getFullYear()) / 1000;
      break;
      
    default:
      console.warn(`Unknown interval type: ${interval.value}, defaulting to day calculation`);
      intervalOffset = (targetCustomDate.getTime() - baseDateObj.getTime()) / (24 * 60 * 60 * 1000);
  }
  
  // Convert interval offset to scroll position
  return intervalOffset * interval.pixelWidth;
}

/**
 * Go to a specific date by updating scroll position
 * @param {Date|CustomDate} targetDate - The date to navigate to
 * @param {object} interval - Current interval object
 * @param {Date|CustomDate|number} baseDate - Reference date
 * @returns {number} The new scroll position
 */
export function handleGoToDate(targetDate, interval, baseDate) {
  return dateToScrollPosition(targetDate, interval, baseDate);
} 